// The event log of the player's deeds (decision 0054: "NPC memory: an event log of the player's deeds, queried by barks
// and dialogue"). A deed is plain data: a kind ("defeated", "quest", "gathered", "talked"…), a subject (an enemy kind, a
// quest id, an item, an NPC), where it happened, an optional tag and a little data. The log keeps the recent entries in a
// ring and counts every (kind, subject) for good, so "has the player ever beaten the Warden" survives the ring.
// Behind {@link EventLogProvider}: an AI-directed story replaces it with a richer memory (decision 0055).
import type { FactValue } from "./conditions";

/** One deed. `t`: the story clock (seconds) when it happened. */
export interface Deed {
  kind: string;
  subject: string;
  /** Where (a region or place id), when known. */
  place?: string;
  /** A tag the game put on what was involved (an enemy spawned for a quest, say). */
  tag?: string;
  /** Where in the world, when known: [x, y, z]. */
  pos?: [number, number, number];
  data?: Record<string, FactValue>;
}

/** A deed as logged. */
export interface LogEntry extends Deed {
  t: number;
  /** Its number in the log (counts up from 1; stable across saves). */
  n: number;
}

/** What to look for: each given field must match (a list matches any of its values). */
export interface LogQuery {
  kind?: string | readonly string[];
  subject?: string | readonly string[];
  place?: string | readonly string[];
  tag?: string | readonly string[];
  /** Only entries at or after this time. */
  since?: number;
  /** Only entries within `radius` of `pos` (entries with no position never match). */
  near?: { pos: [number, number, number]; radius: number };
  /** At most this many, newest first. */
  limit?: number;
}

/** The event log, as the story and the barks use it. */
export interface EventLogProvider {
  /** Log a deed at time `t`. Returns the entry. */
  record(deed: Deed, t: number): LogEntry;
  /** Matching entries still in the log, newest first. */
  query(q?: LogQuery): LogEntry[];
  /** How many matching deeds ever happened, for a kind (and subject); the ring's dropping doesn't lower it. */
  total(kind: string, subject?: string): number;
  /** JSON-safe state, and back. */
  snapshot(): unknown;
  restore(data: unknown): void;
}

const matches = (want: string | readonly string[] | undefined, have: string | undefined): boolean =>
  want === undefined || (typeof want === "string" ? want === have : have !== undefined && want.includes(have));

/** Whether an entry matches a query (ignoring `limit`). */
export function entryMatches(e: LogEntry, q: LogQuery): boolean {
  if (!matches(q.kind, e.kind) || !matches(q.subject, e.subject) || !matches(q.place, e.place) || !matches(q.tag, e.tag)) return false;
  if (q.since !== undefined && e.t < q.since) return false;
  if (q.near) {
    if (!e.pos) return false;
    const d = Math.hypot(e.pos[0] - q.near.pos[0], e.pos[1] - q.near.pos[1], e.pos[2] - q.near.pos[2]);
    if (d > q.near.radius) return false;
  }
  return true;
}

interface RingState {
  entries: LogEntry[];
  next: number;
  /** Lifetime counts: "kind" and "kind|subject". */
  counts: Record<string, number>;
}

/** The standard event log: the last `cap` deeds in a ring, lifetime counts by kind and subject. */
export function ringEventLog(o: { cap: number }): EventLogProvider {
  if (!(o.cap >= 1)) throw new Error("event log: cap must be at least 1");
  let s: RingState = { entries: [], next: 1, counts: {} };
  return {
    record(deed, t) {
      const e: LogEntry = { ...structuredClone(deed), t, n: s.next++ };
      s.entries.push(e);
      if (s.entries.length > o.cap) s.entries.splice(0, s.entries.length - o.cap);
      s.counts[e.kind] = (s.counts[e.kind] ?? 0) + 1;
      const ks = `${e.kind}|${e.subject}`;
      s.counts[ks] = (s.counts[ks] ?? 0) + 1;
      return e;
    },
    query(q = {}) {
      const out: LogEntry[] = [];
      for (let i = s.entries.length - 1; i >= 0; i--) {
        const e = s.entries[i]!;
        if (!entryMatches(e, q)) continue;
        out.push(e);
        if (q.limit !== undefined && out.length >= q.limit) break;
      }
      return out;
    },
    total: (kind, subject) => s.counts[subject === undefined ? kind : `${kind}|${subject}`] ?? 0,
    snapshot: () => structuredClone(s),
    restore(data) {
      const d = data as RingState;
      if (!d || !Array.isArray(d.entries)) throw new Error("event log: not a ring log's snapshot");
      s = structuredClone(d);
    },
  };
}
